import {BasicBitmapCountUp} from "../../display/BasicBitmapCountUp";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {TimelineLite, TweenLite} from "gsap";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {HorribleHeroesGameConfig} from "../../HorribleHeroesGameConfig";
import {HorribleHeroesParsedGameData} from "../../server/data/HorribleHeroesParsedGameData";
import {HorribleHeroesTextStyles} from "../../config/HorribleHeroesTextStyles";
import {SoundManager} from "../../resource/sound/SoundManager";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {HorribleHeroesGameModuleConfig} from "../../HorribleHeroesGameModuleConfig";
import {HorribleHeroesSoundConfig} from "../../HorribleHeroesSoundConfig";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {HorribleHeroes} from "../../HorribleHeroes";

export class HHFreeSpinOutroView extends BaseView {
    private _gameGraphics: PIXI.Sprite;
    private _animation: PIXI.spine.Spine;
    private _smokeAnimation: PIXI.spine.Spine;
    private _totalWin: number = 0;
    private _bitmapText: BasicBitmapCountUp;
    private _dimmer: PIXI.Sprite;
    private _layer: ScalableLayer;
    private _button: ContinueButton;
    private _completeCallback?: () => void;
    private _failureRatio: number = 1;
    private _brokeTheBank: boolean = false;
    private _winCapStripBottom: PIXI.spine.Spine;
    private _winCapStripTop: PIXI.spine.Spine;
    private _winCap: PIXI.spine.Spine;
    private _winCapChainLeft: PIXI.spine.Spine;
    private _winCapChainRight: PIXI.spine.Spine;
    private _capWinX: PIXI.Sprite;
    private _winCapFireworkAnimationLeft: TimelineSprite;
    private _winCapFireworkAnimationRight: TimelineSprite;
    private _winCapSparkleAnimation: TimelineSprite;

    // #582
    private _totalWinBelowStake: boolean = false;

    constructor() {
        super();
        this._layer = StageManager.getLayer(HorribleHeroesGameConfig.instance.LAYERS.FREE_SPIN.name);
    }

    public prepare(data: HorribleHeroesParsedGameData): void {
        this._totalWin = data.totalWin;
        this._failureRatio = data.playedBetValue * 5;
        //#346

        // #414
        this._brokeTheBank = data.brokeTheBank;

        // #582
        this._totalWinBelowStake = HorribleHeroesGameModuleConfig.featurePrice.isWinBelowStake();
    }

    public getStartAnimation(prepareToEnterModeCallback: () => void, completeCallback: () => void): TimelineLite {
        this._completeCallback = completeCallback;

        if (this._brokeTheBank) {
            return this.getWinCapStartAnimation(prepareToEnterModeCallback);
        }

        let countDuration: number = 1.865;
        const delay: number = 1.5;
        const tl: TimelineLite = new TimelineLite();
        this._bitmapText.visible = false;
        this._bitmapText.y = 130;
        this._bitmapText.scale.set(1);

        this._smokeAnimation = this.getOutroAnimation("fsOutroDoorSmoke");
        if (this._totalWin < this._failureRatio) {
            this._animation = this.getOutroAnimation("failFsOutro");
            countDuration = 0; // #478
        } else {
            this._animation = this.getOutroAnimation("fsOutro");
        }

        // https://github.com/nolimitcity/nolimit-slot-launcher/issues/397
        if (this._totalWinBelowStake) {
            countDuration = 0;
        }

        tl.add([
            () => {
                this._smokeAnimation.updateTransform();
                this._smokeAnimation.visible = true;
                this._smokeAnimation.state.setAnimation(0, "fsOutroSmokeStart", false);
                this._layer.addChild(this);
                this._gameGraphics.visible = true;
                this._dimmer.alpha = 0;
            },
            new TweenLite(this._dimmer, 0.2, {alpha: 0.6})
        ], delay - 0.2);

        tl.add([
            () => {
                this._animation.updateTransform();
                this._animation.visible = true;
                this._animation.state.setAnimation(0, "fsOutroStart", false);

                // #478
                if (countDuration > 0) {
                    SoundManager.playCountUp();
                }

                this._bitmapText.preCalculateScale(0, this._totalWin);
                this._bitmapText.getCountAnimation(0, this._totalWin, countDuration);
                this._bitmapText.visible = true;
                this._bitmapText.alpha = 1;
                TweenLite.delayedCall(countDuration, () => {
                    this._button.visible = true;
                    this._button.enable(true);
                    this._layer.addChild(this._button);

                    // #478
                    SoundManager.stopSound(HorribleHeroesSoundConfig.instance.COUNT_UP);
                    if (countDuration > 0) {
                        SoundManager.playCountupEndsFsOutro();
                    } // #582
                    else if (!this._totalWinBelowStake) {
                        SoundManager.playCountUpEndsTiny();
                    }
                });

                // #462, #501
                if (countDuration > 0) {
                    SoundManager.playFSEnds();
                } // #582
                else if (!this._totalWinBelowStake) {
                    SoundManager.playFSEndsBad();
                }

                SoundManager.fadeOutBonusBG();

                SlotGame.keypad.setZeroBetSpinCounter(-1);

                // #429 -> now with FS outro splash has a bg, therefore we won't have #237 occurring and hence this call is placed back here.
                prepareToEnterModeCallback();
            }
        ], delay);

        tl.add(() => {
            this._animation.state.setAnimation(0, "fsOutroLoop", true);
            this._smokeAnimation.state.setAnimation(0, "fsOutroSmokeLoop", true);
        }, delay + 1);

        return tl;
    }

    public getEndAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                // #397
                if (this._animation) {
                    this._animation.visible = false;
                    this._animation.state.clearTracks();
                }

                this._gameGraphics.visible = false;

                if (this._smokeAnimation) {
                    this._smokeAnimation.visible = false;
                    this._smokeAnimation.state.clearTracks();
                }

                // #414
                this.removeWinCapItems();

                // #247
                HorribleHeroesGameModuleConfig.background.exitFS();

                HorribleHeroesGameModuleConfig.featurePrice.reset();
            }
        });

        let duration: number = 0.3;

        this._button.enable(false);
        this._button.visible = false;

        // #414
        if (this._brokeTheBank) {
            duration = this._winCap.spineData.findAnimation("winCapEnd").duration;

            tl.add(() => {
                this.applyWinCapAnimation("End");

                this._winCapChainLeft.state.setAnimation(0, "end", false);
                this._winCapChainRight.state.setAnimation(0, "end", false);

                TweenLite.to(this._capWinX, duration, {alpha: 0});

                // #492
                SoundManager.playCapWinEnds();
                SoundManager.fadeOutBonusBG();
            });
        } else {
            // #397
            TweenLite.to(this._animation, duration, {alpha: 0});
            if (this._smokeAnimation) {
                this._smokeAnimation.state.setAnimation(0, "fsOutroSmokeEnd", false);
            }

            // #410
            SoundManager.playFSStartDoorOpen();

            TweenLite.to(this._bitmapText, duration, {alpha: 0});
        }

        HorribleHeroesGameModuleConfig.stickyWilds.reset();

        HorribleHeroesGameModuleConfig.randomWildView.exitFS(); // #289
        HorribleHeroesGameModuleConfig.logoAnimations.showLogo("");//#381;

        tl.to(this._dimmer, duration, {alpha: 0});

        return tl;
    }

    protected onResize(resizeData: IResizeData): void {
        const buttonPosY: number = resizeData.height - 100;
        //#373
        this._dimmer.width = resizeData.width + 100;
        this._dimmer.height = resizeData.height + 100;
        super.onResize && super.onResize(resizeData);
        this._button.position.set(360 - this._button.width * 0.5, (buttonPosY > 630) ? 630 : buttonPosY);

        // #414
        let chainX: number = 190,
            chainY: number = -310;
        if (NolimitApplication.isLandscape) {
            chainX = 60;
            chainY = 0;
        }
        this._winCapChainLeft.position.set(chainX, chainY);
        this._winCapChainRight.position.set(-chainX, chainY);
    }

    protected initAnimations(): void {
        this._dimmer = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._dimmer.anchor.set(0.5, 0.5);
        this._dimmer.position.set(360, 360);
        this._dimmer.tint = 0X000000;
        this._dimmer.alpha = 0;

        this._gameGraphics = new PIXI.Sprite();
        this._gameGraphics.name = "_gameGraphics";
        this._gameGraphics.anchor.set(0.5, 0.5);
        this._gameGraphics.position.set(360, 360);

        this.createWinCapItems();

        this._bitmapText = new BasicBitmapCountUp(HorribleHeroesTextStyles.FS_OUTRO, 650, new PIXI.Point(0.5, 0.5));
        this._gameGraphics.addChild(this._bitmapText);
        this._bitmapText.position.set(0, 130);

        this._button = new ContinueButton(HorribleHeroes.api.translations.translate("CONTINUE"));
        this._button.addClickCallback(this.internalCompleteCallback);
        this._button.enable(false);
        this._button.visible = false;
        this.addChild(this._dimmer);
        this.addChild(this._gameGraphics);
        //fixes as per #139
    }

    // #414
    private createWinCapItems(): void {
        this._winCapStripBottom = new PIXI.spine.Spine(GameResources.getSpineAsset("winCapStripBottom"));
        this._winCapStripBottom.visible = false;

        this._winCapStripTop = new PIXI.spine.Spine(GameResources.getSpineAsset("winCapStripTop"));
        this._winCapStripTop.visible = false;

        this._winCap = new PIXI.spine.Spine(GameResources.getSpineAsset("winCap"));
        this._winCap.visible = false;

        this._winCapChainLeft = new PIXI.spine.Spine(GameResources.getSpineAsset("bigWinLevel5ChainLeft"));
        this._winCapChainLeft.visible = false;

        this._winCapChainRight = new PIXI.spine.Spine(GameResources.getSpineAsset("bigWinLevel5ChainRight"));
        this._winCapChainRight.visible = false;

        this._winCapFireworkAnimationLeft = new TimelineSprite(GameResources.getTextures("winCapFirework"));
        this._winCapFireworkAnimationLeft.hide();
        this._winCapFireworkAnimationLeft.blendMode = PIXI.BLEND_MODES.ADD;
        this._winCapFireworkAnimationLeft.anchor.set(1, 0.5);
        this._winCapFireworkAnimationLeft.position.set(0, -135);

        this._winCapFireworkAnimationRight = new TimelineSprite(GameResources.getTextures("winCapFirework"));
        this._winCapFireworkAnimationRight.hide();
        this._winCapFireworkAnimationRight.blendMode = PIXI.BLEND_MODES.ADD;
        this._winCapFireworkAnimationRight.anchor.set(0, 0.5);
        this._winCapFireworkAnimationRight.position.set(340, -135);
        this._winCapFireworkAnimationRight.scale.set(-1, 1);

        this._winCapSparkleAnimation = new TimelineSprite(GameResources.getTextures("winCapSparkle"));
        this._winCapSparkleAnimation.hide();
        this._winCapSparkleAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._winCapSparkleAnimation.anchor.set(0.5);
        this._winCapSparkleAnimation.scale.set(1.5, 1.2);
        this._winCapSparkleAnimation.position.set(0, -35);

        this._capWinX = new PIXI.Sprite(GameResources.getTextures("capWinX")[0]);
        this._capWinX.visible = false;
        this._capWinX.position.set(0, 210);
        this._capWinX.anchor.set(0.5);
    }

    private getOutroAnimation(spineTexture: string): PIXI.spine.Spine {
        let spine = new PIXI.spine.Spine(GameResources.getSpineAsset(spineTexture));
        spine.position.set(0, 120);
        spine.visible = false;
        this._gameGraphics.addChild(spine);
        this._gameGraphics.addChild(this._bitmapText);
        return spine;
    }

    private getWinCapStartAnimation(prepareToEnterModeCallback: () => void): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            countDuration: number = 7.773,
            startAnimDuration: number = this._winCap.spineData.findAnimation("winCapStart").duration;

        this._bitmapText.visible = false;
        this._bitmapText.y = -35;
        this._bitmapText.scale.set(0.8);
        this._dimmer.alpha = 0;

        // added this at 0.5, to provide the delay.
        tl.add(() => {
            SlotGame.keypad.setZeroBetSpinCounter(-1);

            this._layer.addChild(this);

            TweenLite.to(this._dimmer, 0.5, {alpha: 1});

            // #492
            SoundManager.playCapWinFadeIn();
        }, 0.5);

        tl.add(() => {
            this._gameGraphics.visible = true;
            this._gameGraphics.addChild(this._winCapStripBottom, this._winCapStripTop, this._winCap, this._winCapChainLeft, this._winCapChainRight, this._winCapFireworkAnimationLeft, this._winCapFireworkAnimationRight, this._winCapSparkleAnimation, this._capWinX, this._bitmapText);

            this.resetWinCapItem(this._winCapStripBottom);
            this.resetWinCapItem(this._winCapStripTop);
            this.resetWinCapItem(this._winCap);
            this.resetWinCapItem(this._winCapChainLeft);
            this.resetWinCapItem(this._winCapChainRight);

            this.applyWinCapAnimation("Hold");
            this._winCapChainLeft.state.setAnimation(0, "loop", true);
            this._winCapChainRight.state.setAnimation(0, "loop", true);

            this._bitmapText.preCalculateScale(0, this._totalWin);
            this._bitmapText.getCountAnimation(0, this._totalWin, countDuration);
            this._bitmapText.visible = true;
            this._bitmapText.alpha = 1;

            // #492
            SoundManager.playCapWinCountUp();

            prepareToEnterModeCallback();
        }, 1);

        tl.add(() => {
            this._winCapFireworkAnimationLeft.getAnimationAutoShowHide(true, true);
            this._winCapFireworkAnimationRight.getAnimationAutoShowHide(true, true);
            this._winCapSparkleAnimation.getAnimationAutoShowHide(true, true);

            TweenLite.to(this._bitmapText.scale, 0.133, {x: 1.3, y: 1.3});
            TweenLite.to(this._bitmapText.scale, 0.2, {x: 0.8, y: 0.8, delay: 0.133});

            // #492
            SoundManager.playCapWinCountUpEnds();
            TweenLite.delayedCall(0.972, () => SoundManager.playBackgroundMusicCapWin());
        }, countDuration + 1);

        // when the final amount is counted up, let it stay on the screen.
        tl.add(() => {
            TweenLite.to(this._bitmapText, 0.2, {y: 210});
            TweenLite.to(this._bitmapText.scale, 0.2, {x: 0, y: 0});
        }, countDuration + 1.8);

        tl.add(() => {
            this.applyWinCapAnimation("Start");

            this._capWinX.visible = true;
            this._capWinX.alpha = 0;
            TweenLite.to(this._capWinX, startAnimDuration, {alpha: 1});
        }, countDuration + 1.9);

        tl.add(() => {
            this.applyWinCapAnimation("Loop");

            this._button.visible = true;
            this._button.enable(true);
            this._layer.addChild(this._button);
        }, countDuration + 2 + startAnimDuration);

        return tl;
    }

    private resetWinCapItem(item: PIXI.spine.Spine): void {
        item.updateTransform();
        item.visible = true;

        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);
    }

    private applyWinCapAnimation(animName: string): void {
        const winCapAnimNamePrefix: string = "winCap",
            loop: boolean = (animName === "Loop");

        this._winCapStripBottom.state.setAnimation(0, winCapAnimNamePrefix + animName, loop);
        this._winCapStripTop.state.setAnimation(0, winCapAnimNamePrefix + animName, loop);
        this._winCap.state.setAnimation(0, winCapAnimNamePrefix + animName, loop);
    }

    private internalCompleteCallback = () => {
        if (this._completeCallback) {
            this._button.enable(false);
            this._completeCallback();
            HorribleHeroesGameModuleConfig.scatterCollecMeter.playScatterCollectState(0);
            //#298
        }
    }

    // #414
    private removeWinCapItems(): void {
        this._winCapStripBottom.visible = false;
        this._winCapStripBottom.state.clearTracks();
        this._winCapStripTop.visible = false;
        this._winCapStripTop.state.clearTracks();
        this._winCap.visible = false;
        this._winCap.state.clearTracks();
        this._winCapChainLeft.visible = false;
        this._winCapChainLeft.state.clearTracks();
        this._winCapChainRight.visible = false;
        this._winCapChainRight.state.clearTracks();

        this._winCapFireworkAnimationLeft.hide();
        this._winCapFireworkAnimationRight.hide();
        this._winCapSparkleAnimation.hide();

        this._capWinX.visible = false;
    }
}
